import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { RotateCcw, RotateCw, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const ASPECTS = [
  { label: "Original", value: null },
  { label: "16:9", value: 16 / 9 },
  { label: "4:3", value: 4 / 3 },
  { label: "1:1", value: 1 },
  { label: "3:4", value: 3 / 4 },
];

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function renderCrop(src, area, rotation) {
  const img = await loadImage(src);
  const rad = (rotation * Math.PI) / 180;
  const sin = Math.abs(Math.sin(rad));
  const cos = Math.abs(Math.cos(rad));
  const bw = img.width * cos + img.height * sin;
  const bh = img.width * sin + img.height * cos;
  const rot = document.createElement("canvas");
  rot.width = bw;
  rot.height = bh;
  const rctx = rot.getContext("2d");
  rctx.translate(bw / 2, bh / 2);
  rctx.rotate(rad);
  rctx.drawImage(img, -img.width / 2, -img.height / 2);
  const out = document.createElement("canvas");
  out.width = Math.round(area.width);
  out.height = Math.round(area.height);
  out.getContext("2d").drawImage(rot, area.x, area.y, area.width, area.height, 0, 0, area.width, area.height);
  return new Promise((resolve) => out.toBlob((b) => resolve(new File([b], "edited.jpg", { type: "image/jpeg" })), "image/jpeg", 0.92));
}

export default function PhotoCropDialog({ src, open, onClose, onSave }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [aspect, setAspect] = useState(null);
  const [area, setArea] = useState(null);
  const [saving, setSaving] = useState(false);
  const [natural, setNatural] = useState(null);
  const onComplete = useCallback((_, px) => setArea(px), []);
  const effAspect = aspect || (natural ? (rotation % 180 ? natural.h / natural.w : natural.w / natural.h) : 4 / 3);

  const save = async () => {
    if (!area) return;
    setSaving(true);
    try {
      onSave(await renderCrop(src, area, rotation));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="photo-crop-dialog">
        <DialogHeader><DialogTitle className="font-head uppercase tracking-tight text-2xl">Crop & Rotate</DialogTitle></DialogHeader>
        <div className="relative w-full h-[50vh] bg-zinc-900">
          <Cropper image={src} crop={crop} zoom={zoom} rotation={rotation} aspect={effAspect} onMediaLoaded={(m) => setNatural({ w: m.naturalWidth, h: m.naturalHeight })}
            onCropChange={setCrop} onZoomChange={setZoom} onCropComplete={onComplete} objectFit="contain" />
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-3">
          {ASPECTS.map((a) => (
            <button key={a.label} type="button" onClick={() => setAspect(a.value)} data-testid={`crop-aspect-${a.label}`}
              className={`border px-2 py-1 text-[11px] uppercase tracking-widest ${aspect === a.value ? "border-black bg-black text-white" : "border-cmdBorder"}`}>
              {a.label}
            </button>
          ))}
          <span className="flex-1" />
          <button type="button" onClick={() => setRotation((r) => (r - 90) % 360)} className="border border-cmdBorder p-1.5" data-testid="crop-rotate-left" title="Rotate left"><RotateCcw className="w-4 h-4" /></button>
          <button type="button" onClick={() => setRotation((r) => (r + 90) % 360)} className="border border-cmdBorder p-1.5" data-testid="crop-rotate-right" title="Rotate right"><RotateCw className="w-4 h-4" /></button>
        </div>
        <label className="flex items-center gap-3 text-xs mt-2">
          <span className="mc-label">Zoom</span>
          <input type="range" min={1} max={4} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} className="flex-1" data-testid="crop-zoom" />
        </label>
        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="border border-cmdBorder px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-1" data-testid="crop-cancel"><X className="w-3 h-3" /> Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="bg-readyGreen text-white px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1 disabled:opacity-50" data-testid="crop-save">
            <Check className="w-3 h-3" /> {saving ? "Saving…" : "Apply"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
